import type { ReactNode } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { SortState } from '../hooks/useServerSort';

/**
 * Sortable `<th>` for the handful of registers that render their own `<table>` markup instead of
 * going through `TwDataTable` (expandable batch/ledger views, mostly). It renders the SAME arrows,
 * the same asc → desc → off click cycle and the same "icon always trails the label" rule the shared
 * table uses, so a bespoke table's header is indistinguishable from a DataTable one.
 *
 * Pair it with `useServerSort` exactly like a DataTable — the click drives a backend ORDER BY, never
 * a local re-sort of the loaded page:
 *
 *   const { sortState, onSortChange, sort } = useServerSort({ defaultSort: { key: 'qty', dir: 'desc' } });
 *   <Th sortKey="qty" state={sortState} onSort={onSortChange} align="right">Qty</Th>
 */
export function SortArrows({ dir }: { dir: 'asc' | 'desc' | null }) {
  const cls = 'size-3.5 shrink-0';
  if (dir === 'asc') return <ArrowUp className={`${cls} text-brand-600 dark:text-brand-400`} strokeWidth={2.5} />;
  if (dir === 'desc') return <ArrowDown className={`${cls} text-brand-600 dark:text-brand-400`} strokeWidth={2.5} />;
  return <ArrowUpDown className={`${cls} text-line-strong`} strokeWidth={2} />;
}

export function SortableTh({
  sortKey, state, onSort, children, className = '', align = 'left',
}: {
  /** Column identity sent to the backend — must match a key in that endpoint's sort whitelist. */
  sortKey: string;
  state: SortState;
  onSort: (next: SortState) => void;
  children: ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
}) {
  const dir = state?.key === sortKey ? state.dir : null;
  // Same three-step cycle as TwDataTable: unsorted → asc → desc → back to the endpoint's default.
  const next: SortState = dir === null ? { key: sortKey, dir: 'asc' } : dir === 'asc' ? { key: sortKey, dir: 'desc' } : null;
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : '';
  return (
    <th
      className={`cursor-pointer select-none hover:text-content ${className}`}
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
      onClick={() => onSort(next)}
    >
      <span className={`inline-flex w-full items-center gap-1 ${justify}`}>
        {children}
        <SortArrows dir={dir} />
      </span>
    </th>
  );
}
